import { useTranslate } from 'react-admin';
import {
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Tooltip,
  IconButton,
  InputAdornment,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export interface ImapFormData {
  imapEnabled: boolean;
  imapAddress: string;
  imapPort: number;
  imapLogin: string;
  imapPassword: string;
  imapEnableSsl: boolean;
}

export const defaultImapData: ImapFormData = {
  imapEnabled: false,
  imapAddress: '',
  imapPort: 993,
  imapLogin: '',
  imapPassword: '',
  imapEnableSsl: true,
};

interface ImapFormFieldsProps {
  data: ImapFormData;
  onChange: <K extends keyof ImapFormData>(key: K, value: ImapFormData[K]) => void;
  disabled?: boolean;
  errors?: Record<string, string>;
  onClearError?: (field: string) => void;
}

export function ImapFormFields({ data, onChange, disabled, errors = {}, onClearError }: ImapFormFieldsProps) {
  const translate = useTranslate();

  const handleChange = <K extends keyof ImapFormData>(key: K, value: ImapFormData[K]) => {
    onChange(key, value);
    onClearError?.(key);
  };

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 8 }}>
        <TextField
          fullWidth
          label={translate('resources.channel.wizard.email.imapAddress')}
          value={data.imapAddress}
          onChange={(e) => handleChange('imapAddress', e.target.value)}
          placeholder="imap.gmail.com"
          required
          disabled={disabled}
          error={!!errors.imapAddress}
          helperText={errors.imapAddress}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <TextField
          fullWidth
          label={translate('resources.channel.wizard.email.imapPort')}
          value={data.imapPort}
          onChange={(e) => handleChange('imapPort', parseInt(e.target.value) || 993)}
          type="number"
          required
          disabled={disabled}
          error={!!errors.imapPort}
          helperText={errors.imapPort}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          label={translate('resources.channel.wizard.email.imapLogin')}
          value={data.imapLogin}
          onChange={(e) => handleChange('imapLogin', e.target.value)}
          required
          disabled={disabled}
          error={!!errors.imapLogin}
          helperText={errors.imapLogin || translate('resources.channel.wizard.email.usernameHelper')}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          label={translate('resources.channel.wizard.email.imapPassword')}
          value={data.imapPassword}
          onChange={(e) => handleChange('imapPassword', e.target.value)}
          type="password"
          required
          disabled={disabled}
          error={!!errors.imapPassword}
          helperText={errors.imapPassword || translate('resources.channel.wizard.email.passwordHelper')}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip
                  title={translate('resources.channel.wizard.email.appPasswordTooltip')}
                  arrow
                  placement="top"
                >
                  <IconButton size="small" tabIndex={-1} edge="end">
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid size={12}>
        <FormControlLabel
          control={
            <Switch
              checked={data.imapEnableSsl}
              onChange={(e) => handleChange('imapEnableSsl', e.target.checked)}
              disabled={disabled}
            />
          }
          label={translate('resources.channel.wizard.email.imapSsl')}
        />
      </Grid>
    </Grid>
  );
}
